import { useCallback, useMemo, useState } from 'react';
import { getActivities } from '@conversion-craft/workflow-definitions';
import { toast } from 'sonner';

import { useTranslation } from '@/hooks/useTranslation';
import { Modal } from './Modal';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useModalContext } from '@/hooks/useModalContext';
import { InputPasteButton } from '../InputPasteButton';
import { LoadingIndicator } from '../common/LoadingIndicator';
import { useCreateMWWorkflow } from '@/hooks/message-whale/workflow/useCreateMWWorkflow';
import { EmptyState } from '../common/EmptyState';
import { cn } from '@/lib/utils';
import { ActivityIcon } from '@/screens/workflow/components/activity/ActivityIcon';
import { MwWorkflowTemplateSelector } from '../MwWorkflowTemplateSelector';
import { getLocalizedField } from '@/utils/i18n';
import { WorkflowTemplate } from '@/types/workflow';
import { MWWorkflow } from '@/types/message-whale/mwWorkflow';

const steps = [0, 1];

const triggers = getActivities().filter((activity) => activity.type === 'trigger');

export const CreateMWWorkflowModal = () => {
  const { t } = useTranslation('common');
  const { t: workflowT } = useTranslation('workflow');
  const { closeModal } = useModalContext();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  
  const [name, setName] = useState('');
  const [type, setType] = useState<'empty' | 'template'>('empty');
  const [selectedTrigger, setSelectedTrigger] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);

  const isSaveVisible = currentStepIndex === 1;
  const isNextVisible = currentStepIndex === 0;

  const isSaveEnabled = useMemo(() => {
    if (type === 'empty') {
      return name.length > 2 && selectedTrigger !== null;
    }

    if (type === 'template') {
      return selectedTemplate !== null;
    }

    return false;
  }, [name, type, selectedTrigger, selectedTemplate]);

  const isNextEnabled = useMemo(() => {
    const hasMoreSteps = currentStepIndex < steps.length - 1;
    
    if (!hasMoreSteps) {
      return false
    }

    if (currentStepIndex === 1 && type === 'empty') {
      return name.length > 2;
    }

    if (currentStepIndex === 1 && type === 'template') {
      return selectedTemplate !== null;
    }

    return true;
  }, [name, currentStepIndex, selectedTrigger, type]);

  const handleClose = () => {
    closeModal('createNewMWWorkflow');
    setCurrentStepIndex(0);
    setName('');
    setSelectedTrigger(null);
    setType('empty');
  };

  const { mutate: createWorkflow, isPending: isCreatingWorkflow } = useCreateMWWorkflow({
    onSuccess: () => {
      toast.success(t('workflowCreatedSuccessfully'));
      handleClose();
    },
    onError: () => {
      toast.error(t('failedToCreateWorkflow'));
    },
  });

  const handleSave = useCallback(() => {
    const createData: Partial<MWWorkflow> = {
      name,
    };

    if (selectedTrigger) {
      createData.trigger = selectedTrigger;
    }

    if (selectedTemplate) {
      createData.name = getLocalizedField(selectedTemplate, 'name');
      createData.trigger = selectedTemplate.trigger;
      // createData.data = selectedTemplate.data;
    }

    createWorkflow(createData);
  }, [createWorkflow, name, selectedTrigger, selectedTemplate]);

  const handlePaste = async (
    setValue: React.Dispatch<React.SetStateAction<string>>
  ) => {
    try {
      const text = await navigator.clipboard.readText();
      setValue(text);
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const handleNextStep = useCallback(() => {
    const hasNextStep = currentStepIndex === 0;

    if (currentStepIndex === 1 && type === 'empty') {
      if (name.length < 3) {
        toast.error(t('xFieldIsRequired', { name: t('name') }));
        return;
      }
    }

    if (hasNextStep) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  }, [currentStepIndex, name]);

  const handleTemplateSelect = useCallback((template: WorkflowTemplate) => {
    setSelectedTemplate(template);
  }, []);

  return (
    <Modal
      className="md:max-h-[600px] md:max-w-[700px] lg:max-w-[800px]"
      modalType="createNewMWWorkflow"
      title={t('createNewWorkflow')}
      onClose={handleClose}
    >
      <div className="space-y-4">
        {
          currentStepIndex === 0 && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                <p className="text-sm font-medium">
                  {t('selectWorkflowType')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('workflowTypeDescription')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <EmptyState
                  className={cn(
                    'cursor-pointer hover:border-gray-400 flex flex-col items-center justify-center gap-4',
                    type === 'empty' && 'border-vbm hover:border-vbm'
                  )}
                  onClick={() => {
                    setType('empty');
                  }}
                >
                  <img src="/undraw_no-data.svg" className="w-16 md:w-32 mx-auto" />
                  <p className="text-muted-foreground text-sm font-medium tabular-nums">
                    {t('empty')}
                  </p>
                </EmptyState>

                <EmptyState
                  className={cn(
                    'cursor-pointer hover:border-gray-400 flex flex-col items-center justify-center gap-4',
                    type === 'template' && 'border-vbm hover:border-vbm'
                  )}
                  onClick={() => {
                    setType('template');
                  }}
                >
                  <img src="/undraw_portfolio.svg" className="w-16 md:w-32 mx-auto" />
                  <p className="text-muted-foreground text-sm font-medium tabular-nums">
                    {t('template')}
                  </p>
                </EmptyState>
              </div>
            </div>
          )
        }

        {
          (currentStepIndex === 1 && type === 'empty') && (
            <div className="flex flex-col gap-4">
              <div className="relative">
                <Label htmlFor="workflow-name">{t('name')}</Label>
                <Input
                  id="workflow-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <InputPasteButton onClick={() => handlePaste(setName)} />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {
                  triggers.map((trigger) => (
                    <div
                      key={trigger.name}
                      className={cn(
                        'cursor-pointer border rounded-md p-4 border-dashed hover:border-gray-400 grid grid-cols-8 items-center justify-center gap-4',
                        selectedTrigger === trigger.name && 'border-vbm hover:border-vbm border-solid bg-vbm/10'
                      )}
                      onClick={() => {
                        setSelectedTrigger(trigger.name);
                      }}
                    >
                      <ActivityIcon className="col-span-2" group={trigger.group} name={trigger.name} />
                      <div className="text-muted-foreground text-sm font-medium col-span-6 flex flex-col gap-1">
                        <span className="text-xs">
                          {workflowT(`groups.${trigger.group}`)}
                        </span>
                        <span>
                          {workflowT(`activities.${trigger.name}.name`)}
                        </span>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          )
        }

        {
          currentStepIndex === 1 && type === 'template' && (
            <MwWorkflowTemplateSelector
              selectedTemplate={selectedTemplate}
              onSelect={handleTemplateSelect}
            />
          )
        }
      </div>
      <div className="flex justify-center space-x-4 mt-4">
        <Button
          variant="outline"
          className="w-32"
          onClick={() => setCurrentStepIndex((prev) => prev - 1)}
          disabled={currentStepIndex === 0}
        >
          {t('previous')}
        </Button>

        {
          isNextVisible && (
            <Button
              variant="outline"
              className="w-32"
              onClick={handleNextStep}
              disabled={!isNextEnabled}
            >
              {t('next')}
            </Button>
          )
        }

        {
          isSaveVisible && (
            <Button
              onClick={handleSave}
              disabled={isCreatingWorkflow || !isSaveEnabled}
              className="flex gap-1 w-32"
              variant="conversionCraft"
            >
              {isCreatingWorkflow && <LoadingIndicator size="small" />}
              {t('save')}
            </Button>
          )
        }
      </div>
      {/* <div className="space-y-4">
        <div className="relative">
          <Label htmlFor="workflow-name">{t('name')}</Label>
          <Input
            id="workflow-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <InputPasteButton onClick={() => handlePaste(setName)} />
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={handleClose}>
            {t('close')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isCreatingWorkflow}
            className="flex gap-1"
            variant="conversionCraft"
          >
            {isCreatingWorkflow && <LoadingIndicator size="small" />}
            {t('save')}
          </Button>
        </DialogFooter>
      </div> */}
    </Modal>
  );
};
